"use client";

/**
 * P2-04 · <WishlistHeart> — a labelled toggle button layered above the card
 * link. The button box is 44px (P2-DB04 §05) around a 32px visual circle.
 *
 * P14-10 · It is now connected to something. P2 shipped it "visual-only …
 * persisted for real behind `flags.wishlist` in P5", and P5 built the store
 * (`useWishlist`: localStorage for guests, `WishlistItem` for accounts, merged
 * on sign-in) and the PDP button that uses it — but never came back for the
 * card. So the heart on every grid filled in on click, said "Added to
 * wishlist" to a screen reader, and forgot on reload: a control that lies, one
 * flag flip away from being seen by customers.
 *
 * The store is read here rather than through an `onToggle` prop, because the
 * prop is what failed. `ProductCard` declared `onWishlistToggle` for three
 * phases and no production call site ever passed it; a control that only works
 * when four layers of grid remember to thread a callback is a control that
 * will be dead again by the next surface that renders a card.
 *
 * P18-19 · The `wishlist` flag is asked here rather than by the card, for the
 * same reason the store is read here: the gate belongs with the control, and a
 * shell that asks no flags needs no hooks and can render on the server. The
 * check sits in a wrapper so a store with the flag off never mounts the store
 * (and so never fires its query) — exactly as when the card decided.
 */
import { cn } from "~/lib/utils";
import { useOptionalFeatureFlag } from "~/components/feature-flags/provider";
import { useSignedIn } from "~/components/auth/signed-in-provider";
import { useWishlist } from "~/lib/wishlist/use-wishlist";

export interface WishlistHeartProps {
  productId: string;
  productTitle: string;
  /** Still honoured, for callers that want to react (a toast, a count). */
  onToggle?: (saved: boolean) => void;
  className?: string;
}

export function WishlistHeart(props: WishlistHeartProps) {
  const enabled = useOptionalFeatureFlag("wishlist");
  if (!enabled) return null;
  return <WishlistHeartControl {...props} />;
}

function WishlistHeartControl({
  productId,
  productTitle,
  onToggle,
  className,
}: WishlistHeartProps) {
  const signedIn = useSignedIn();
  // Only mounted behind `flags.wishlist` (see above), so the store is always
  // wanted where it is mounted.
  const wishlist = useWishlist({ signedIn });
  const saved = wishlist.has(productId);
  return (
    <button
      type="button"
      aria-pressed={saved}
      aria-label={
        saved
          ? `Remove ${productTitle} from wishlist`
          : `Add ${productTitle} to wishlist`
      }
      onClick={() => {
        // `toggle` is optimistic with rollback and returns the new state, so
        // the button re-renders from the store rather than a second copy of
        // the truth kept in local state — which is what made the old one look
        // like it worked.
        //
        // SR-19 · Toggle FIRST, then notify. `onToggle?.(wishlist.toggle(id))`
        // never evaluates its argument when `onToggle` is undefined — which is
        // every production card — so the heart did nothing at all on staging.
        const next = wishlist.toggle(productId);
        onToggle?.(next);
      }}
      className={cn(
        "z-[2] flex size-11 items-center justify-center focus-visible:outline-none",
        // SR-19 · Tailwind v4 applies stacked variants left to right, so the
        // old `[&>span]:focus-visible:` meant `span:focus-visible` and never
        // matched: a keyboard user saw no focus at all on the heart.
        "focus-visible:[&>span]:ring-2 focus-visible:[&>span]:ring-[var(--ring)]",
        className,
      )}
    >
      <span
        className={cn(
          // Tread · a full 44 px Concrete disc on the white tile from `md` up;
          // the bare heart on phones.
          "flex size-11 items-center justify-center rounded-full transition-colors md:bg-bg",
          saved ? "text-danger" : "text-fg-muted hover:text-fg",
        )}
      >
        <svg
          aria-hidden
          viewBox="0 0 24 24"
          className="size-5"
          fill={saved ? "currentColor" : "none"}
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
        </svg>
      </span>
    </button>
  );
}
